import { CommonModule } from '@angular/common';
import { Component, QueryList, signal, ViewChild, ViewChildren } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { dayjs } from '@karve.it/core';
import { Store } from '@ngrx/store';
import { Calendar, CalendarModule } from 'primeng/calendar';import { filter, map, switchMap, take } from 'rxjs';

import { ViewDatesGQL, ViewDatesQuery } from '../../../generated/graphql.generated';
import { zonesFeature } from '../../core/zones/zones.reducer';
import { HighlightDatesDirective, DateHighlight } from '../../shared/directives/highlight-dates.directive';

import { buildUtilizationHighlights } from './highlight-dates.util';
import { ScheduleControlsService } from './schedule-controls.service';

/**
 * The schedule page's date-window picker — the pill (calendar icon + input)
 * LEFT of the controls bar's Today / < > buttons (Ray; the breadcrumb's
 * last crumb shows the current tab's name instead). Binds the shared
 * {@link ScheduleControlsService} signals, so the pill and the tabs show
 * and drive one window.
 *
 * Variants keyed to the active tab:
 * - `week` slug → one click on a date selects the whole displayed week
 *   (Mon–Sun, FC firstDay 1); a second, distinct click narrows to an
 *   explicit custom span (Ray).
 * - `month` slug → pick the month, not a date (the picker anchors on that
 *   month's first day).
 * - range-mode tabs (map, ops-readiness) → start + end over two clicks.
 * - day / dispatch → a single date.
 */
@Component({
    selector: 'app-crumb-date-picker',
    standalone: true,
    imports: [CommonModule, FormsModule, CalendarModule, HighlightDatesDirective],
    templateUrl: './crumb-date-picker.component.html',
    styleUrls: ['./crumb-date-picker.component.scss'],
})
export class CrumbDatePickerComponent {
    /* The classic constructor parameter injection pattern (CodeRabbit round
     * 22 / coding guidelines: avoid inject() in class fields). */
    constructor(
        protected controls: ScheduleControlsService,
        private store: Store,
        private viewDatesGQL: ViewDatesGQL,
    ) {}

    /** The active p-calendar variant (only one renders at a time). */
    @ViewChildren('cal') calendars: QueryList<Calendar>;

    /** Per-day utilization highlights for the picker panel's day cells
     *  (the dashed rings — the same format as the schedules page's removed
     *  inline picker and the new-opportunity calculator's picker; the
     *  picker fetches its own viewDates rows per browsed month — a
     *  3-month span so adjacent-month days get rings too — Ray: "it
     *  wasn't loading when we moved around the date picker"). */
    readonly highlights = signal<DateHighlight[]>([]);

    /** Fetched windows, keyed by their CENTER month 'YYYY-MM' — each entry
     *  holds the rows of a 3-month span (previous, current, next — Ray:
     *  "pull 3 months at a time... so it's nicer to move back and forth").
     *  Browsed months stay cached — navigating back and forth costs no
     *  further request — and adjacent-month days inside the panel get
     *  rings too. */
    private readonly monthRows = new Map<string, ViewDatesQuery['viewDates']>();

    /** Month bucket (key form) whose panel month is displayed. A late
     *  response for a month the user already left is dropped. */
    private browsedMonthKey = '';

    /** Panel opened: load the utilization rings for the displayed window's
     *  month (the picker's input value's month). */
    onPanelShow(): void {
        const d = this.controls.date();
        const anchor = (d ? dayjs(d) : dayjs());
        this.loadMonth(anchor.year(), anchor.month() + 1);
    }

    /** The user navigated months inside the panel; PrimeNG supplies the
     *  1-based month (the calculator's monthChanged mapping does
     *  `month - 1` for dayjs too). */
    onPanelMonthChange(ev: { month: number; year: number }): void {
        this.loadMonth(ev.year, ev.month);
    }

    /** Fetch (or serve from cache) the browsed month's viewDates rows and
     *  rebuild the ring highlights from them. One request covers a
     *  3-month span — the browsed month plus the previous and the next —
     *  so every day in the panel (adjacent greyed days included) shows
     *  its utilization ring (Ray). Rows are cached under the browsed
     *  month's key; navigating between already-visited months re-serves
     *  from cache. */
    private loadMonth(year: number, month1Based: number): void {
        const key = `${year}-${String(month1Based).padStart(2, '0')}`;
        this.browsedMonthKey = key;
        const cached = this.monthRows.get(key);
        if (cached) {
            this.highlights.set(buildUtilizationHighlights(cached ?? []));
            return;
        }
        const anchor = dayjs().year(year).month(month1Based - 1);
        const start = anchor.subtract(1, 'month').startOf('month');
        const end = anchor.add(1, 'month').endOf('month');
        // Same data pattern as the new-opportunity calculator: the current
        // app zone, includeEvents off, prophet on (tier colours for the
        // rings).
        this.store.select(zonesFeature.selectCurrentAppZone).pipe(
            filter(Boolean),
            take(1),
            switchMap((zone) => this.viewDatesGQL.fetch({
                startDate: start.format('YYYY-MM-DD'),
                endDate: end.format('YYYY-MM-DD'),
                zone: (zone as any).id,
                includeEvents: false,
                prophet: true,
            }).pipe(
                map((res) => res.data?.viewDates?.filter((r): r is NonNullable<typeof r> => r != null) ?? []),
                take(1),
            )),
        ).subscribe({
            next: (rows) => {
                // A stale month the user already navigated away from: drop.
                if (this.browsedMonthKey !== key) { return; }
                this.monthRows.set(key, rows);
                this.highlights.set(buildUtilizationHighlights(rows));
            },
            error: () => { /* the picker renders plain dates without rings */ },
        });
    }

    /** The week tab's one-click date picker; hidden programmatically after
     *  the span pick (the range stays open after the first click). */
    @ViewChild('weekCal') weekCal: any;

    /** The picker variant keyed to the active tab (see the class doc). */
    get pickerKind(): 'single' | 'week' | 'month' | 'range' {
        const slug = this.controls.activeTabSlug();
        if (slug === 'week') { return 'week'; }
        if (slug === 'month') { return 'month'; }
        return this.controls.isRangeMode() ? 'range' : 'single';
    }

    /** One click target for the whole pill — text, icon, whitespace —
     *  focuses the picker's input, which shows the overlay (showOnFocus).
     *  stopPropagation: the pill's outer containers also listen for clicks
     *  (the breadcrumb item template swaps it into PrimeNG's crumb anchor,
     *  whose handler would otherwise prevent the input's focus).
     *  A focused input produces no further focus event — PrimeNG's own
     *  input handler only OPENS the overlay on focusin (CodeRabbit round
     *  22) — so after focusing, open the overlay explicitly when it is not
     *  already visible. */
    openPicker(event: Event): void {
        event.stopPropagation();
        (event.currentTarget as HTMLElement | null)?.querySelector<HTMLInputElement>('input')?.focus();
        // A focused input produces no further focus event, and PrimeNG's own
        // input handler only OPENS the overlay on focusin (CodeRabbit round
        // 22) — toggle() opens it explicitly when it is not already visible.
        const cal = this.calendars?.first;
        if (cal && !cal.overlayVisible) {
            cal.toggle();
        }
    }

    /** Day-mode single date (day / dispatch). */
    get dayDate(): Date {
        return this.controls.date();
    }
    set dayDate(value: Date) {
        if (value) {
            this.controls.setDate(value);
        }
    }

    /** Range-mode [start, end] for the PrimeNG selectionMode="range" pickers
     *  (week, map, ops-readiness). While only one end is picked (length 1
     *  or a null end) the stored window is left alone so the range isn't
     *  half-applied. Memoized like the old bar's model: a fresh array per
     *  read with two-way binding loop change detection forever (page
     *  freeze). */
    /** The week tab's first pick, kept OPEN so a distinct second click can
     *  complete an explicit custom span (CodeRabbit round 22: committing a
     *  full Mon–Sun range while the overlay is open makes PrimeNG restart
     *  the next click as a new range, so the second click could never
     *  complete). While set, the model exposed to PrimeNG stays open-ended
     *  [weekPending, null]; cleared when the overlay closes (onClose). */
    weekPending: Date | null = null;
    /** MEMOIZED open-ended model for the pending pick — a fresh array per
     *  read with the two-way binding would loop change detection forever
     *  (page freeze), same as the completed-window cache below. */
    private weekPendingModel: Date[] | null = null;

    get rangeDates(): Date[] {
        if (this.weekPending && this.pickerKind === 'week') {
            if (!this.weekPendingModel || this.weekPendingModel[0] !== this.weekPending) {
                this.weekPendingModel = [this.weekPending, null];
            }
            return this.weekPendingModel;
        }
        this.weekPendingModel = null;
        const start = this.controls.date();
        const end = this.controls.dateTo() ?? start;
        const startMs = start?.getTime();
        const endMs = end?.getTime();
        if (!this.rangeDatesCache
            || this.rangeDatesCacheStartMs !== startMs
            || this.rangeDatesCacheEndMs !== endMs) {
            this.rangeDatesCache = [start, end];
            this.rangeDatesCacheStartMs = startMs;
            this.rangeDatesCacheEndMs = endMs;
        }
        return this.rangeDatesCache;
    }
    set rangeDates(value: Date[]) {
        // PrimeNG (17) range picks: the first click emits [start, null];
        // the second completes [start, end] or restarts. The week tab keeps
        // its first pick PENDING (see {@link weekPending}) — one click still
        // selects the whole displayed week (Mon–Sun, FC firstDay 1 — Ray);
        // a distinct second click completes that pending pick into an
        // explicit custom span and closes the picker.
        const start = value?.[0];
        if (!start) { return; }
        if (this.pickerKind === 'week') {
            const completed = value.length === 2 && value[1] && !this.sameDay(value[0], value[1]);
            if (this.weekPending) {
                if (completed) {
                    // Second, distinct click: the explicit custom span wins.
                    this.controls.setDate(value[0]);
                    this.controls.setDateTo(value[1]);
                    this.weekPending = null;
                    queueMicrotask(() => this.weekCal?.hideOverlay());
                } else if (!value[1]) {
                    // PrimeNG restarted the range with no pending end?? treat
                    // the pick as a fresh first click.
                    this.pickWeek(start);
                } else {
                    // Same-day completion picked again: re-snap the week,
                    // keep the pending pick so a next distinct click can
                    // still narrow.
                    this.pickWeek(value[0]);
                }
            } else if (completed) {
                // A completed pair with no pending pick is an explicit span
                // (e.g. the model arrived completed) — apply it as-is.
                this.controls.setDate(value[0]);
                this.controls.setDateTo(value[1]);
            } else {
                // First click: snap the window to the picked date's week and
                // KEEP the overlay open (hideOnDateTimeSelect=false) so the
                // second click can complete a custom span.
                this.pickWeek(start);
            }
            return;
        }
        // PrimeNG emits [start, null] for the first end of a range pick —
        // applying it would clear the window's end (and possibly trigger a
        // reload) mid-pick (CodeRabbit).
        if (value.length === 2 && value[0] && value[1]) {
            this.controls.setDate(value[0]);
            this.controls.setDateTo(value[1]);
        }
    }
    private rangeDatesCache: Date[] | null = null;
    private rangeDatesCacheStartMs?: number;
    private rangeDatesCacheEndMs?: number;

    /** Month view of the schedule (month tab): picking a MONTH (not a
     *  date — Ray) anchors on that month's first day. The month grid and
     *  the bar's < > (which step by month) cover the rest of the month. */
    get monthDate(): Date {
        return this.controls.date();
    }
    set monthDate(value: Date) {
        if (!value) { return; }
        this.controls.setDate(dayjs(value).startOf('month').toDate());
    }

    private sameDay(a: Date, b: Date): boolean {
        return !!a && !!b && dayjs(a).isSame(b, 'day');
    }

    /** Apply the week tab's one-click snap (Ray): the picked date's whole
     *  displayed week, Monday to Sunday, and remember the pick as the
     *  pending selection so a distinct second click can narrow it. */
    private pickWeek(start: Date): void {
        const pick = dayjs(start).startOf('day');
        const weekStart = pick.subtract((pick.day() + 6) % 7, 'day');
        this.controls.setDate(weekStart.toDate());
        this.controls.setDateTo(weekStart.add(6, 'day').toDate());
        this.weekPending = pick.toDate();
    }
}